import { Flame, Crown, Settings, Trophy } from "lucide-react";
import { calculateTier, TIER_COLORS, TIER_PROGRESSION, type EvolveTier } from "@/types/evolve";
import { Button } from "@/components/ui/button";

interface EvolveHeaderProps {
  userName: string;
  totalWorkouts: number;
  onSettingsClick?: () => void;
  onLeaderboardClick?: () => void;
}

export function EvolveHeader({ userName, totalWorkouts, onSettingsClick, onLeaderboardClick }: EvolveHeaderProps) {
  const { currentTier, workoutsToNext, progress, skulls } = calculateTier(totalWorkouts);
  const tierColor = TIER_COLORS[currentTier];
  
  // Calculate workouts in current tier bracket for display
  const currentTierIndex = TIER_PROGRESSION.findIndex(t => t.tier === currentTier);
  const currentTierWorkouts = TIER_PROGRESSION[currentTierIndex]?.workoutsRequired || 0;
  const nextTierWorkouts = TIER_PROGRESSION[currentTierIndex + 1]?.workoutsRequired || 0;
  const workoutsInTier = totalWorkouts - currentTierWorkouts;
  const workoutsNeeded = nextTierWorkouts - currentTierWorkouts;

  return (
    <header className="bg-background px-3 sm:px-4 py-2 sm:py-3 sticky top-0 z-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br ${tierColor} flex items-center justify-center evolve-glow`}>
              <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-foreground" />
            </div>
            {skulls > 0 && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-background rounded-full flex items-center justify-center border border-primary">
                <span className="text-[10px] font-bold">💀{skulls}</span>
              </div>
            )}
          </div>
          <div>
            <h1 className="font-display text-base sm:text-lg font-bold tracking-wide">
              EVOLVE
            </h1>
            <p className="text-xs text-muted-foreground">
              Welcome, <span className="text-primary font-medium">{userName}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Tier Badge - clickable for leaderboard */}
          <button 
            onClick={onLeaderboardClick}
            className="flex flex-col items-end hover:opacity-80 transition-opacity"
          >
            <div className={`tier-badge bg-gradient-to-r ${tierColor} flex items-center gap-1`}>
              <Crown className="w-3 h-3" />
              {currentTier}
              <Trophy className="w-3 h-3 ml-1 opacity-70" />
            </div>
            {workoutsToNext !== null && (
              <p className="text-[10px] text-muted-foreground mt-1">
                {workoutsToNext} workouts to next tier
              </p>
            )}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-3">
        <div className="h-1 bg-secondary rounded-full overflow-hidden">
          <div 
            className={`h-full bg-gradient-to-r ${tierColor} transition-all duration-500`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </header>
  );
}
